import {
  Component,
  effect,
  inject,
  OnInit,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AppService } from '@shared/services/app.service';
import { FiltrosReporte } from '@shared/components/filtros-reporte/filtros-reporte';
import { UiSelect, UiDialog, UiDatepicker } from '@shared/components/ui';
import { ToastService } from '@shared/services/toast.service';
import { ITablaColumna } from '@core/interfaces/tabla.interface';
import { IPaginar } from '@core/interfaces/paginacion/paginacion.interface';
import {
  IReporteKardexFiltroReq,
  IReporteKardexRes,
} from '@core/interfaces/request-response/kardex.dto.interface';
import { FormField, form, required } from '@angular/forms/signals';
import { EAtajoRangoTiempo } from '@core/enums/atajoRangoTiempo.enum';
import { calcularAtajoRangoFecha } from '@shared/utils/functions.utils';
import { FormErrorMessagePipe } from '@shared/pipes/form-error-message.pipe';
import { ETipoMovimientoKardex } from '@core/enums/tipoMovimientoKardex.enum';
import { IOptionsSelect } from '@core/interfaces/options.select.interface';
import { EmpleadoApiService } from '@core/services/api/empleado-api.service';
import { IEmpleadoData } from '@core/interfaces/entidades/empleado.entidad';
import { Tabla } from '@shared/components/tabla/tabla';
import { KardexApiService } from '@core/services/api/kardex-api.service';
import { Paginacion } from '@shared/components/paginacion/paginacion';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { environment } from '@env/environment';
import { SucursalService } from '@shared/services/sucursal.service';
import { ISucursalData } from '@core/interfaces/entidades/sucursal.entidad';
import { NombreSucursalPipe } from '../../../shared/pipes/nombre-sucursal.pipe';
import { descargarArchivoDesdeResponse } from '@shared/utils/functions.utils';

@Component({
  selector: 'app-reporte-kardex',
  imports: [
    FormsModule,
    FormField,
    FiltrosReporte,
    UiSelect,
    UiDialog,
    UiDatepicker,
    FormErrorMessagePipe,
    Tabla,
    Paginacion,
    DatePipe,
    CurrencyPipe,
    NombreSucursalPipe,
  ],
  templateUrl: './reporte-kardex.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './reporte-kardex.css',
})
export class ReporteKardex implements OnInit {
  constructor() {
    effect(() => {
      if (this.sucursalService.sucursalReady() && this.appService.authReady()) {
        this.cargarOpcionesSelect();
        this.cargarOpcionesSucursal();
        this.cargarEmpleados();
      }
    });
  }
  ngOnInit(): void {
    this.appService.setPanelHeader({
      title: 'Reporte de Kardex',
      subtitle: 'Visualiza el reporte de kardex',
      showBackButton: true,
      icon: 'bi bi-box-seam',
    });
    this.columnasTabla = [
      {
        key: 'fecha',
        titulo: 'Fecha',
        pipe: new DatePipe(environment.locale),
        pipeFormat: 'dd/MM/yyyy HH:mm:ss',
      },
      { key: 'tipo_movimiento', titulo: 'Movimiento', template: this.movimientoTemplate() },
      { key: 'cantidad', titulo: 'Cantidad' },
      {
        key: 'precio_unitario',
        titulo: 'Precio Unitario',
        pipe: new CurrencyPipe(environment.locale, 'PEN'),
      },
      { key: 'total', titulo: 'Total', pipe: new CurrencyPipe(environment.locale, 'PEN') },
      { key: 'saldo', titulo: 'Saldo', pipe: new CurrencyPipe(environment.locale, 'PEN') },
      { key: 'concepto', titulo: 'Concepto' },
      { key: 'producto.nombre', titulo: 'Producto' },
      { key: 'empleado.nombre_completo', titulo: 'Empleado' },
      { key: 'sucursal_id', titulo: 'Sucursal', template: this.sucursalColumnTemplate() },
    ];
  }
  // servicios
  private appService = inject(AppService);
  private toastService = inject(ToastService);
  private empleadoApiService = inject(EmpleadoApiService);
  private kardexApiService = inject(KardexApiService);
  private sucursalService = inject(SucursalService);
  canSelectSucursal = this.sucursalService.canSelectSucursal;
  // datos
  private itemsPorPagina = 18;
  ETipoMovimientoKardex = ETipoMovimientoKardex;
  maxDate = new Date(new Date().setHours(23, 59, 59, 999));
  minDate = new Date(new Date().setFullYear(new Date().getFullYear() - 1));
  columnasTabla: ITablaColumna[] = [];
  paginacion = signal<IPaginar<IReporteKardexRes> | null>(null);
  // resumenReporte = signal<IResumenKardexRes | null>(null);
  opcionesTipoMovimiento = signal<IOptionsSelect<ETipoMovimientoKardex | null>[]>([]);
  modal_detalles_kardex = signal<boolean>(false);
  kardex_seleccionado: IReporteKardexRes | null = null;

  private requestFiltro: IReporteKardexFiltroReq | null = null;
  // filtros
  opcionesSucursal = signal<IOptionsSelect<ISucursalData | null>[]>([]);
  opcionesEmpleado = signal<IOptionsSelect<IEmpleadoData | null>[]>([]);
  formFiltro = form(
    signal({
      rangoFecha: null as Date[] | null,
      tipo_movimiento: null as ETipoMovimientoKardex | null,
      empleado: null as IEmpleadoData | null,
      sucursal: null as ISucursalData | null,
    }),
    (x) => {
      required(x.rangoFecha, { message: 'El rango de fechas es obligatorio' });
    },
  );
  // metodos
  cambiarPagina(pagina: number) {
    if (this.requestFiltro) {
      this.kardexApiService
        .paginarReporte(this.requestFiltro, pagina, this.itemsPorPagina)
        .subscribe({
          next: (paginar) => {
            this.paginacion.set(paginar);
          },
          error: () => {
            this.toastService.show(
              'top-right',
              'error',
              'Error',
              'No se pudo cargar el reporte de kardex con los filtros seleccionados',
            );
          },
        });
    }
  }
  private checkInvalidForm() {
    if (this.formFiltro().invalid()) {
      this.formFiltro.rangoFecha().markAsTouched();
      this.toastService.show(
        'top-right',
        'error',
        'Error',
        'Por favor completa los campos obligatorios',
      );
      return true;
    }
    return false;
  }
  aplicarFiltros(event: Event) {
    event.preventDefault();
    if (this.checkInvalidForm()) return;
    const rq = this.buildRequestFiltro();
    if (!rq) return;

    this.requestFiltro = rq;
    this.cambiarPagina(1);
  }

  exportarExcel() {
    if (this.checkInvalidForm()) return;
    const rq = this.buildRequestFiltro();
    if (!rq) return;

    this.kardexApiService.exportarReporteExcel(rq).subscribe({
      next: (response) => {
        descargarArchivoDesdeResponse(response, 'reporte-kardex.xlsx');
        this.toastService.show('top-right', 'success', 'Excel', 'Reporte kardex descargado.');
      },
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'No se pudo exportar el reporte kardex.',
        );
      },
    });
  }

  private buildRequestFiltro(): IReporteKardexFiltroReq | null {
    const { rangoFecha, tipo_movimiento, empleado, sucursal } = this.formFiltro().controlValue();
    if (!rangoFecha || rangoFecha.length !== 2) return null;

    return {
      fecha_inicio: rangoFecha![0],
      fecha_fin: rangoFecha![1],
      tipo_movimiento: tipo_movimiento || undefined,
      empleado_id: empleado ? empleado.id : undefined,
      sucursal_id: sucursal ? sucursal.id : undefined,
    };
  }
  EAtajoRangoTiempo = EAtajoRangoTiempo;
  atajoRangoFecha(tiempo: EAtajoRangoTiempo) {
    const { inicio, fin } = calcularAtajoRangoFecha(tiempo);
    this.formFiltro.rangoFecha().value.set([inicio, fin]);
  }
  limpiarFiltros() {
    this.formFiltro.rangoFecha().value.set(null);
    this.formFiltro.tipo_movimiento().value.set(null);
    this.formFiltro.empleado().value.set(null);
    if (this.canSelectSucursal()) {
      this.formFiltro.sucursal().value.set(null);
    }
  }
  verDetallesKardex(row: IReporteKardexRes) {
    this.kardex_seleccionado = row;
    this.modal_detalles_kardex.set(true);
  }
  private cargarEmpleados() {
    this.empleadoApiService.cargar().subscribe({
      next: (empleados) => {
        this.opcionesEmpleado.set(
          empleados.map((empleado) => ({
            label: empleado.nombre_completo,
            value: empleado,
          })),
        );
        // agregamos opción de todos
        this.opcionesEmpleado.update((options) => [
          {
            label: 'Todos los empleados',
            value: null,
          },
          ...options,
        ]);
      },
      error: () => {
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          'No se pudieron cargar los empleados para el filtro',
        );
      },
    });
  }
  private cargarOpcionesSelect() {
    this.opcionesTipoMovimiento.set(
      Object.values(ETipoMovimientoKardex).map((tipo) => ({
        label: tipo === null ? 'Todos' : tipo,
        value: tipo,
      })),
    );
    this.opcionesTipoMovimiento.update((options) => [
      {
        label: 'Todos',
        value: null,
      },
      ...options,
    ]);
  }
  private cargarOpcionesSucursal() {
    const sucursales = this.sucursalService.sucursales();
    const restringidaId = this.sucursalService.sucursalRestringidaId();

    if (restringidaId != null) {
      const restringida = sucursales.find((s) => s.id === restringidaId);
      this.opcionesSucursal.set(
        restringida ? [{ label: restringida.nombre, value: restringida }] : [],
      );
      this.formFiltro.sucursal().value.set(restringida ?? null);
      return;
    }

    this.opcionesSucursal.set(
      sucursales.map((sucursal) => ({
        label: sucursal.nombre,
        value: sucursal,
      })),
    );
    this.opcionesSucursal.update((options) => [
      {
        label: 'Todas las sucursales',
        value: null,
      },
      ...options,
    ]);
  }

  movimientoTemplate = viewChild('movimientoTemplate');
  private sucursalColumnTemplate = viewChild('sucursalColumnTemplate');
}
